import React from 'react';
import clsx from 'clsx';
import '@/blocks/FeatureLinksIconsBlock/FeatureLinksIconsBlock.styles.css';
import {
  IFeatureLinksIconsCardProps,
  IProps,
} from '@/blocks/FeatureLinksIconsBlock/FeatureLinksIconsBlock.interface';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import Icon from '@/elements/Icon/Icon.component';

// Default SVG background as data URL
// const DEFAULT_ICON_BACKGROUND = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="75" height="76" viewBox="0 0 75 76" fill="none">
// <path d="M74.6049 38.2687C74.6049 58.285 64.8576 75.637 44.74 75.637C24.6224 75.637 0.395081 62.7872 0.395081 42.7709C0.395081 22.7546 9.91615 0 30.0338 0C50.1514 0 74.6049 13.2815 74.6049 38.2687Z" fill="#FF65D2" fill-opacity="0.5"/>
// </svg>`)}`;

// btoa encoding is not returning consistent results with ssr, so using a pre-encoded string instead.
const DEFAULT_ICON_BACKGROUND = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI3NSIgaGVpZ2h0PSI3NiIgdmlld0JveD0iMCAwIDc1IDc2IiBmaWxsPSJub25lIj4KPHBhdGggZD0iTTc0LjYwNDkgMzguMjY4N0M3NC42MDQ5IDU4LjI4NSA2NC44NTc2IDc1LjYzNyA0NC43NCA3NS42MzdDMjQuNjIyNCA3NS42MzcgMC4zOTUwODEgNjIuNzg3MiAwLjM5NTA4MSA0Mi43NzA5QzAuMzk1MDgxIDIyLjc1NDYgOS45MTYxNSAwIDMwLjAzMzggMEM1MC4xNTE0IDAgNzQuNjA0OSAxMy4yODE1IDc0LjYwNDkgMzguMjY4N1oiIGZpbGw9IiNGRjY1RDIiIGZpbGwtb3BhY2l0eT0iMC41Ii8+Cjwvc3ZnPg==";


const FeatureLinksIconsCard = (card: IFeatureLinksIconsCardProps) => {
  const { cardType, icon, iconBackground, description, heading, link } = card;
  return (
    <div
      className={clsx(
        'featureLinksIconsBlock__card',
        `featureLinksIconsBlock__card--${cardType}`,
      )}
      role="listitem"
    >
      {icon && (
        <div
          className="featureLinksIconsBlock__card-icon-container"
          style={{
            backgroundImage: `url(${iconBackground || DEFAULT_ICON_BACKGROUND})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <Icon icon={icon} className="featureLinksIconsBlock__card-icon" />
        </div>
      )}
      <div className="featureLinksIconsBlock__card-content">
        {heading &&
          (link ? (
            <a
              href={link.href}
              target={link.target || undefined}
              title={link.title || undefined}
              aria-label={link.ariaLabel || undefined}
              className="featureLinksIconsBlock__card-link"
            >
              <Heading
                level={3}
                className={clsx("featureLinksIconsBlock__card-heading", {
                  'featureLinksIconsBlock__card-heading--xl': cardType !== 'compact' && description,
                })}
                html={heading}
              />
            </a>
          ) : (
            <Heading
              level={3}
              className={clsx("featureLinksIconsBlock__card-heading", {
                  'featureLinksIconsBlock__card-heading--xl': cardType !== 'compact' && description,
                })}
              html={heading}
            />
          ))}
        {cardType !== 'compact' && description && (
          <Text
            className="featureLinksIconsBlock__card-description"
            html={description}
          />
        )}
      </div>
    </div>
  );
};

const FeatureLinksIconsBlock: React.FC<IProps> = (props) => {
  const {
    heading,
    description,
    buttons,
    cards,
    cardType,
    columns = 3,
    className,
    ...blockWrapperProps
  } = props;

  // Clamp columns between 2 and 6
  const columnCount = Math.min(Math.max(columns, 2), 6);

  // Responsive: On mobile, always horizontal and 1 column
  // Use CSS for layout switching

  return (
    <BlockWrapper
      className={clsx('featureLinksIconsBlock', className)}
      {...blockWrapperProps}
    >
      <ContentWrapper>
        <ContentColumn>
          <div className="featureLinksIconsBlock__header">
            {heading && (
              <Heading
                level={2}
                className="featureLinksIconsBlock__heading"
                html={heading}
              />
            )}
            {buttons && buttons.length > 0 && (
              <div className="featureLinksIconsBlock__cta__container">
                {buttons.map((button, idx) => (
                  <Button
                    key={`feature-links-icons-block-cta-${idx}`}
                    {...button}
                    className="featureLinksIconsBlock__cta"
                  />
                ))}
              </div>
            )}
          </div>
          {description && (
            <Text
              className="featureLinksIconsBlock__description"
              html={description}
            />
          )}
          <div
            className={clsx(
              'featureLinksIconsBlock__cards',
              `featureLinksIconsBlock__cards--${cardType}`,
              `featureLinksIconsBlock__cards--columns-${columnCount}`,
            )}
            role="list"
          >
            {cards.map((card, idx) => (
              <FeatureLinksIconsCard
                key={`feature-links-icons-block-card-${idx}`}
                {...card}
                cardType={cardType}
              />
            ))}
          </div>
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};

export { FeatureLinksIconsBlock };
